import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Crown, Sparkles, Check, ArrowRight, X } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

/**
 * v2.71 — Post-signup upgrade nudge for FREE CLIENTS only.
 *
 * Behaviour rules (from product spec):
 * - Shows ONCE per newly-created client account (keyed by user.id in localStorage)
 * - Suppressed for: admin, team, dealer, builder, paid/active subscriptions
 * - 600ms delay after auth state lands so it doesn't flash during navigation
 * - Closable, non-blocking (route still navigates underneath)
 * - Survives refresh: marked-shown flag persists in localStorage, not in state
 */
const SHOWN_KEY_PREFIX = "he_post_signup_plans_shown__";

export default function PostSignupPlansModal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Strict allow-list: only free clients (no active subscription)
    if (user.role !== "client") return;
    const sub = user.subscription || {};
    if (sub.active && sub.tier && sub.tier !== "free") return;
    // Already shown for this user in this browser?
    const key = `${SHOWN_KEY_PREFIX}${user.id}`;
    try {
      if (localStorage.getItem(key) === "1") return;
    } catch (_) { /* private mode → just skip persistence */ }
    const t = setTimeout(() => {
      setOpen(true);
      try { localStorage.setItem(key, "1"); } catch (_) { /* noop */ }
    }, 600);
    return () => clearTimeout(t);
  }, [user?.id, user?.role, user?.subscription?.active, user?.subscription?.tier]);

  const goToPricing = () => {
    setOpen(false);
    nav("/pricing");
  };
  const goToPlansPreview = () => {
    setOpen(false);
    nav("/plans-preview");
  };

  if (!user || user.role !== "client") return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md rounded-2xl p-0 overflow-hidden border-2 border-[#FDE68A]" data-testid="post-signup-plans-modal">
        {/* Premium gradient header */}
        <div className="relative bg-gradient-to-br from-[#0055FF] via-[#3B82F6] to-[#7C3AED] text-white p-6 pb-12">
          <button
            onClick={() => setOpen(false)}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="post-signup-plans-close"
          >
            <X size={14} />
          </button>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] uppercase tracking-[0.2em] font-bold mb-3">
            <Sparkles size={10} /> Welcome to HouseEntry
          </div>
          <h2 className="font-heading font-bold text-2xl tracking-tight leading-tight">
            Aapka free account ready hai!
          </h2>
          <p className="text-[13px] text-white/85 mt-2 leading-relaxed">
            Premium plans unlock karke serious property hunting shuru karein.
          </p>
        </div>

        {/* Body */}
        <div className="bg-white px-6 pt-7 pb-6 -mt-6 rounded-t-2xl relative">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-[#FDE68A] to-[#F59E0B] text-white shadow-md shadow-yellow-500/30 absolute -top-6 left-6">
            <Crown size={20} />
          </div>

          <div className="mt-5 mb-1">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#92400E]">Premium ke saath milta hai</div>
          </div>
          <ul className="space-y-2.5 my-3">
            {[
              { t: "Exact location & Map View — har property", s: "Pin location + nearby map" },
              { t: "Direct dealer contact, no callbacks", s: "Save hours of follow-up" },
              { t: "Premium-only deals — exclusive listings", s: "Off-market opportunities" },
              { t: "Priority enquiry response", s: "30-min turnaround promise" },
            ].map((f, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-[#16A34A]/10 text-[#16A34A] flex items-center justify-center shrink-0 mt-0.5">
                  <Check size={12} />
                </span>
                <div>
                  <div className="text-[13.5px] font-semibold text-zinc-900 leading-tight">{f.t}</div>
                  <div className="text-[11.5px] text-zinc-500 leading-relaxed">{f.s}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-2 mt-5">
            <button
              onClick={goToPricing}
              className="group w-full h-11 inline-flex items-center justify-center gap-1.5 rounded-xl btn-gradient text-[13.5px] font-bold shadow-md shadow-blue-500/20 hover:shadow-lg active:scale-[0.99] transition-all"
              data-testid="post-signup-plans-upgrade"
            >
              See pricing & upgrade <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={goToPlansPreview}
              className="w-full h-10 inline-flex items-center justify-center rounded-xl text-[12.5px] font-semibold text-zinc-600 hover:text-[#0055FF] hover:bg-[#EEF4FF] transition-colors"
              data-testid="post-signup-plans-compare"
            >
              Compare plans first
            </button>
            <button
              onClick={() => setOpen(false)}
              className="w-full h-9 inline-flex items-center justify-center text-[11.5px] text-zinc-400 hover:text-zinc-600 transition-colors"
              data-testid="post-signup-plans-skip"
            >
              Maybe later
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
